import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { qualityApi, type QualityDocItem, type QualityDocUpdatePayload } from '../services/quality';

function normalizeLegacyHtml(text: string): string {
  const withLineBreaks = text
    .replace(/\\r/g, '')
    .replace(/\\n/g, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<\/div>\s*<div>/gi, '\n\n')
    .replace(/<li>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '');

  if (typeof window === 'undefined') {
    return withLineBreaks;
  }

  const decoder = document.createElement('textarea');
  decoder.innerHTML = withLineBreaks;
  return decoder.value;
}

function renderLines(text: string | null) {
  if (!text) {
    return null;
  }

  return normalizeLegacyHtml(text).split('\n').map((line, index) => (
    <p key={`${index}-${line}`} className="whitespace-pre-wrap text-[15px] leading-7 text-slate-700">
      {line}
    </p>
  ));
}

function DetailBlock({ title, text }: { title: string; text: string | null }) {
  if (!text) {
    return null;
  }

  return (
    <section className="rounded-[24px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      <h2 className="mb-3 text-lg font-semibold tracking-[-0.02em] text-slate-950">{title}</h2>
      <div className="space-y-3">{renderLines(text)}</div>
    </section>
  );
}

function EditField({
  label,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
}) {
  return (
    <label className="space-y-2">
      <div className="text-sm font-semibold text-slate-700">{label}</div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={7}
          className="min-h-[160px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] leading-6 text-slate-900 shadow-sm outline-none transition focus:border-amber-500"
        />
      ) : (
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 shadow-sm outline-none transition focus:border-amber-500"
        />
      )}
    </label>
  );
}

function toFormState(item: QualityDocItem): QualityDocUpdatePayload {
  return {
    chapter: item.chapter ?? '',
    title: item.title,
    intro: item.intro ?? '',
    description: item.description ?? '',
    legislation: item.legislation ?? '',
    recommendations: item.recommendations ?? '',
    hazardSummary: item.hazardSummary ?? '',
    category: item.category ?? '',
  };
}

export function QualityDocDetailPage() {
  const { id } = useParams();
  const toast = useToast();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [item, setItem] = useState<QualityDocItem | null>(null);
  const [form, setForm] = useState<QualityDocUpdatePayload>({
    chapter: '',
    title: '',
    intro: '',
    description: '',
    legislation: '',
    recommendations: '',
    hazardSummary: '',
    category: '',
  });

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    qualityApi.doc(id)
      .then((data) => {
        setItem(data.item);
        setForm(toFormState(data.item));
      })
      .catch(() => toast.error('Kunde inte ladda dokumentet'))
      .finally(() => setLoading(false));
  }, [id, toast]);

  async function handleSave() {
    if (!id) {
      return;
    }
    setSaving(true);
    try {
      const data = await qualityApi.updateDoc(id, form);
      setItem(data.item);
      setForm(toFormState(data.item));
      setEditing(false);
      toast.success('Dokumentet sparades');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte spara dokumentet';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (!item) {
      return;
    }
    setForm(toFormState(item));
    setEditing(false);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-3">
          <Link to="/quality/docs" className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">
            Tillbaka
          </Link>

          {loading ? (
            <div className="text-slate-500">Laddar dokument...</div>
          ) : item ? (
            <div className="space-y-1">
              <div className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500">{item.chapter ?? '-'}</div>
              <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[32px]">{item.title}</h1>
            </div>
          ) : (
            <div className="text-slate-500">Dokumentet hittades inte.</div>
          )}
        </div>

        {item && canWrite('quality') ? (
          editing ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Sparar...' : 'Spara'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
            >
              Redigera
            </button>
          )
        ) : null}
      </div>

      {item ? (
        editing ? (
          <div className="grid gap-4">
            <section className="rounded-[24px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
              <div className="grid gap-4">
                <EditField label="Kapitel" value={form.chapter} onChange={(value) => setForm((current) => ({ ...current, chapter: value }))} />
                <EditField label="Titel" value={form.title} onChange={(value) => setForm((current) => ({ ...current, title: value }))} />
              </div>
            </section>

            <section className="rounded-[24px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
              <div className="grid gap-4">
                <EditField label="Inledning" value={form.intro} onChange={(value) => setForm((current) => ({ ...current, intro: value }))} multiline />
                <EditField label="Beskrivning av arbetssätt" value={form.description} onChange={(value) => setForm((current) => ({ ...current, description: value }))} multiline />
                <EditField label="Lagstiftning" value={form.legislation} onChange={(value) => setForm((current) => ({ ...current, legislation: value }))} multiline />
                <EditField label="Branschens rekommendationer" value={form.recommendations} onChange={(value) => setForm((current) => ({ ...current, recommendations: value }))} multiline />
                <EditField label="Faror" value={form.hazardSummary} onChange={(value) => setForm((current) => ({ ...current, hazardSummary: value }))} multiline />
              </div>
            </section>
          </div>
        ) : (
          <div className="space-y-4">
            <DetailBlock title="Inledning" text={item.intro} />
            <DetailBlock title="Beskrivning av arbetssätt" text={item.description} />
            <DetailBlock title="Lagstiftning" text={item.legislation} />
            <DetailBlock title="Branschens rekommendationer" text={item.recommendations} />
            <DetailBlock title="Faror" text={item.hazardSummary} />
          </div>
        )
      ) : null}
    </div>
  );
}
